import { isoWeekStart } from "./calendar-period.js";
/**
 * PB-2941 — the reporting CADENCE derived from a KPI's `reportingFrequency` and
 * the instant a reading was observed.
 *
 * NOTHING HERE IS STORED. There is no period table, no `period_start`/`period_end`,
 * no schedule anchor and no iteration id — the KPI carries a frequency and the
 * ledger carries `observedAt`, and everything below is a pure function of those
 * two. That is the whole design: a rolling cadence anchored on the reading itself,
 * so "next due" means "a frequency after the last reading", never "the next
 * Monday". A fixed calendar schedule is a different product decision and would
 * need a column to hold its anchor.
 *
 * DETERMINISTIC AND UTC, for the same reason `formatDate` is: a KPI page must
 * read identically for two people in different zones and under test. `Date`'s
 * UTC accessors do the work; nothing here consults the local clock.
 */
/** Locale-independent by construction — `toLocaleString` would make the label
 * depend on the reader's environment, which is exactly what these surfaces avoid. */
const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
/** What a KPI with no stated cadence reads. Not an empty string and not a dash:
 * "no frequency" is a fact about the KPI, and saying so is more honest than
 * leaving a blank the reader has to interpret. */
export const UNSCHEDULED_PERIOD_LABEL = "Unscheduled";
function parseInstant(iso) {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}
/** `YYYY-MM-DD` from a UTC instant. Padded rather than sliced off `toISOString`
 * only because the same three parts are needed separately below. */
function utcDate(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
/**
 * `d` plus `months` calendar months, CLAMPED to the target month's last day.
 *
 * `Date.UTC(2026, 1, 31)` silently rolls into March, so a monthly KPI observed
 * on 31 January would report a "next due" of 3 March — a date the reader would
 * read as a bug in the cadence rather than in the arithmetic. Day 0 of the
 * FOLLOWING month is that month's last day, which also settles February in a
 * leap year without a leap-year branch.
 */
function addMonthsClamped(d, months) {
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + months;
    const lastDayOfTargetMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(d.getUTCDate(), lastDayOfTargetMonth), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds()));
}
function addDays(d, days) {
    return new Date(d.getTime() + days * 86_400_000);
}
/**
 * The reporting period a reading belongs to, as a label.
 *
 * `every_two_weeks` is anchored on the READING, not on a shared calendar: a
 * fortnight has no natural boundary the way a month or a quarter does, and the
 * platform stores no anchor to derive one from. "Fortnight from 2026-08-16" is
 * what can honestly be said; "Fortnight 16 of 2026" would be inventing an
 * organization-wide start date nobody chose.
 *
 * Returns {@link UNSCHEDULED_PERIOD_LABEL} for a KPI with no frequency, and null
 * only for an instant that cannot be parsed — a caller then renders its own
 * empty state rather than a period label built from a broken date.
 */
export function reportingPeriodLabel(observedAt, frequency) {
    if (frequency === null)
        return UNSCHEDULED_PERIOD_LABEL;
    const d = parseInstant(observedAt);
    if (!d)
        return null;
    switch (frequency) {
        case "daily":
            return utcDate(d);
        case "weekly":
            return `Week of ${utcDate(isoWeekStart(d))}`;
        case "every_two_weeks":
            return `Fortnight from ${utcDate(d)}`;
        case "monthly":
            return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
        case "quarterly":
            return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`;
    }
}
/**
 * When the next reading is due: the last reading's instant plus one reporting
 * frequency.
 *
 * ROLLING, not scheduled. A weekly KPI observed three days ago is due in four
 * days, because the cadence is measured from the reading rather than from a
 * fixed weekday — that is the version of this feature the client approved, and
 * it is the only one the current data model can state truthfully.
 *
 * Null for an unscheduled KPI (there is no next due date to invent) and for an
 * unparseable instant.
 */
export function nextReportingDueAt(observedAt, frequency) {
    if (frequency === null)
        return null;
    const d = parseInstant(observedAt);
    if (!d)
        return null;
    switch (frequency) {
        case "daily":
            return addDays(d, 1).toISOString();
        case "weekly":
            return addDays(d, 7).toISOString();
        case "every_two_weeks":
            return addDays(d, 14).toISOString();
        case "monthly":
            return addMonthsClamped(d, 1).toISOString();
        case "quarterly":
            return addMonthsClamped(d, 3).toISOString();
    }
}
//# sourceMappingURL=kpi-reporting-period.js.map